import { isDeepEqual } from '@attain-apps/data-utils'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import * as rxjs from 'rxjs'

import type * as variableOverrides from '../../../../../../variable-overrides'
import type * as aliases from '../../../../../aliases'
import type * as currentRendering from '../../../feature'
import type * as widgets from '../../widgets'
import type { Feature } from '../feature'

/**
 * Time in milliseconds to debounce the rendering process either on
 * widgetVariables or other relevant changes
 *
 * @see {@link variableOverrides.Feature}
 */
const DEBOUNCE_TIME = 500

export const triggerSlideoutWidgetRendering = store.effects.create(
  (
    feature: Feature,
    currentRenderingFeature: currentRendering.Feature,
    widgetsFeature: widgets.Feature,
    variablesFeature: variableOverrides.Feature,
    aliasesFeature: aliases.Feature,
  ) =>
    feature.selectors.widgetRef.asObservable.pipe(
      rxjsUtils.latestFrom(widgetRef => ({
        pageContext: currentRenderingFeature.selectors.context.asObservable,
        widgetRef: rxjs.of(widgetRef),
      })),
      rxjs.filter(
        (
          data,
        ): data is typeof data & {
          pageContext: NonNullable<typeof data.pageContext>
          widgetRef: NonNullable<typeof data.widgetRef>
        } => !!data.widgetRef && !!data.pageContext,
      ),
      rxjs.switchMap(({ pageContext, widgetRef }) =>
        aliasesFeature.selectors
          .getWidgetId(widgetRef.widget)
          .asObservable.pipe(
            rxjs.switchMap(
              widgetId =>
                variablesFeature.selectors.getVariableOverrides(
                  widgetId
                    ? {
                        widget: {
                          appId: pageContext.appId,
                          pageId: pageContext.pageId,
                          refId: widgetRef.id,
                          scenarioId: pageContext.scenarioId,
                          widgetId,
                        },
                      }
                    : undefined,
                ).asObservable,
            ),
            rxjs.distinctUntilChanged(isDeepEqual),
            rxjs.map(widgetVariables => ({
              pageContext,
              widgetRef,
              widgetVariables,
            })),
          ),
      ),
      rxjs.debounceTime(DEBOUNCE_TIME),
      rxjs.map(({ pageContext, widgetVariables, widgetRef }) =>
        widgetsFeature.actions.render
          .from('triggerSlideoutWidgetRendering (effect)')
          .create({
            pageContext,
            ref: widgetRef,
            variables: {
              ...widgetRef.variables,
              ...widgetVariables,
            },
          }),
      ),
    ),
)
